import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-credentials-view',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  template: `
  <p-toast></p-toast>
  <div class="credential-view-container">
    <!-- Botões de ação -->
    <div class="toolbar">
      <button pButton
        icon="pi pi-download"
        label="Baixar PDF"
        (click)="downloadPdf()"
        class="p-button-primary p-button-sm">
      </button>
      <span class="file-info" *ngIf="!isLoading">{{fileName}} ({{fileSize}})</span>
    </div>

    <!-- Loading -->
    <div class="loading" *ngIf="isLoading && !error">
      <p-progressSpinner></p-progressSpinner>
      <p>Carregando credencial...</p>
      <small>Isso pode levar alguns segundos</small>
    </div>

    <!-- Erro -->
    <div class="error" *ngIf="error && !isLoading">
      <i class="pi pi-exclamation-triangle error-icon"></i>
      <h3>{{ error }}</h3>
      <p>Tente fazer o download do arquivo:</p>
      <button pButton 
              label="Baixar PDF" 
              (click)="downloadPdf()"
              class="p-button-primary">
      </button>
      <button pButton 
        label="Fechar" 
        (click)="close()"
        class="p-button-secondary ml-2">
      </button>
      <div class="expand-action">
        <p-button
          [icon]="isFullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"
          (onClick)="toggleFullscreen()"
          styleClass="expand-btn"
          pTooltip="{{isFullscreen ? 'Sair de tela cheia' : 'Tela cheia'}}"
          tooltipPosition="bottom"
          [text]="true"
          [rounded]="true">
        </p-button>
      </div>
      <i class="pi pi-external-link expand-icon ml-2" (click)="expandView()" title="Expandir tela" style="cursor:pointer;font-size:1.5rem;vertical-align:middle;"></i>
    </div>

    <!-- PDF - USANDO IFRAME ao invés de EMBED -->
    <div class="pdf-container" *ngIf="showPdf && !isLoading && !error">
      <iframe #pdfFrame
              [src]="pdfUrl"
              type="application/pdf"
              width="100%"
              height="100%"
              frameborder="0"
              (load)="onPdfLoaded()"
              style="border: none;">
      </iframe>
      
      <!-- Fallback message para navegadores sem suporte -->
      <div class="fallback-message" *ngIf="showFallback">
        <p>Seu navegador não suporta visualização embutida de PDF.</p>
        <button pButton 
                label="Abrir em nova janela" 
                (click)="openInNewWindow()"
                class="p-button-primary">
        </button>
        <button pButton 
                label="Baixar PDF" 
                (click)="downloadPdf()"
                class="p-button-secondary ml-2">
        </button>
      </div>
    </div>
  </div>
`,
  styles: [`


        .expand-icon {
          color: #495057;
          transition: color 0.2s;
        }
        .expand-icon:hover {
          color: #007ad9;
        }
    .credential-view-container {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      background: #f5f5f5;
    }
    
    .toolbar {
      padding: 10px 15px;
      background: white;
      border-bottom: 1px solid #ddd;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      z-index: 100;
    }
    
    .file-info {
      font-size: 0.9rem;
      color: #666;
    }
    
    .loading, .error {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 2rem;
    }
    
    .error {
      background: #fff5f5;
      border-radius: 8px;
      margin: 1rem;
    }
    
    .error-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }
    
    .error h3 {
      color: #721c24;
      margin-bottom: 1rem;
    }
    
    .ml-2 {
      margin-left: 8px;
    }
    
    .pdf-container {
      flex: 1;
      position: relative;
      overflow: hidden;
    }
    
    embed {
      display: block;
      width: 100%;
      height: 100%;
    }
    
    .fallback-message {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10;
    }
    
    @media (max-width: 768px) {
      .toolbar {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
      }
      
      .file-info {
        font-size: 0.8rem;
      }
    }
  `]
})
export class CredentialsViewComponent implements OnInit, AfterViewInit, OnDestroy {
isFullscreen = false;
  toggleFullscreen(): void {
    // Tenta pegar o container do modal PrimeNG
    let modalRoot = document.querySelector('.p-dialog') as HTMLElement;
    if (!modalRoot) {
      // fallback para o container interno
      modalRoot = document.querySelector('.credential-view-container') as HTMLElement;
    }
    if (!modalRoot) return;
    if (!this.isFullscreen) {
      if (modalRoot.requestFullscreen) {
        modalRoot.requestFullscreen();
      } else if ((modalRoot as any).webkitRequestFullscreen) {
        (modalRoot as any).webkitRequestFullscreen();
      } else if ((modalRoot as any).msRequestFullscreen) {
        (modalRoot as any).msRequestFullscreen();
      }
      this.isFullscreen = true;
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
      this.isFullscreen = false;
    }
  }
    expandView(): void {
      if (this.blobUrl) {
        window.open(this.blobUrl, '_blank', 'noopener,noreferrer');
      }
    }
  @ViewChild('pdfFrame') pdfFrame!: ElementRef; // Mudou de pdfEmbed para pdfFrame
  
  pdfUrl: SafeResourceUrl | string = '';
  isLoading = true;
  error: string | null = null;
  showPdf = false;
  showFallback = false;
  fileName = '';
  fileSize = '';

  
  
  private blob: Blob | null = null;
  private blobUrl: string | null = null;
  private loadCheckInterval: any;
  private fallbackCheckTimeout: any;

  

  constructor(
    public config: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private sanitizer: DomSanitizer,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializePdf();
  }

  ngAfterViewInit(): void {
    // Inicia verificação de carregamento após a view ser inicializada
    setTimeout(() => {
      this.startLoadDetection();
    }, 100);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

 private initializePdf(): void {
  try {
    const data = this.config.data as { blob: Blob; fileName: string };
    
    if (!data?.blob) {
      throw new Error('Arquivo PDF não fornecido');
    }

    this.blob = data.blob;
    this.fileName = data.fileName || 'credencial.pdf';
    this.fileSize = this.formatFileSize(this.blob.size);

    console.log('📊 Inicializando PDF:', {
      fileName: this.fileName,
      size: this.blob.size,
      type: this.blob.type
    });

    // Cria URL do blob
    this.blobUrl = URL.createObjectURL(this.blob);

/* Parâmetros úteis para visualização de PDF via URL
Controles de Interface
  toolbar=0 - Oculta a barra de ferramentas
  navpanes=0 - Oculta o painel de navegação (índice)
  scrollbar=0 - Oculta a barra de rolagem
  statusbar=0 - Oculta a barra de status
  messages=0 - Oculta mensagens

Comportamento de Visualização
  view=Fit - Ajusta à página
  view=FitH - Ajusta à largura
  view=FitV - Ajusta à altura
  view=FitB - Ajusta ao box de contorno
  zoom=100 - Define o nível de zoom (ex: 50, 75, 100, 125, 150)
  pagemode=none - Modo de página
  pagemode=bookmarks - Mostra marcadores
  pagemode=thumbs - Mostra miniaturas
  pagemode=attachments - Mostra anexos

Navegação e Páginas
  page=1 - Define a página inicial (número da página)
  nameddest=section1 - Vai para um destino nomeado
  search=keyword - Realiza uma busca pelo termo

Configurações Avançadas
  Comportamento de Roteamento
  #page=2 - Usando hash (não recarrega a página)
  &page=2 - Usando query parameter (pode recarregar)

Outras Opções Úteis
  disableprefetch=1 - Desabilita pré-busca
  disablesidebar=1 - Desabilita a barra lateral
  disablescrollbars=1 - Desabilita barras de rolagem
*/
    
    // Adiciona parâmetros para ocultar ferramentas do PDF
    const urlWithParams = `${this.blobUrl}#toolbar=0&navpanes=0&scrollbar=0&zoom=150`;
    
    // Usa sanitização para segurança
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlWithParams);
    
    // Marca que o PDF está pronto para ser mostrado
    this.showPdf = true;
    
    // Força atualização da view
    this.cdr.detectChanges();

  } catch (err) {
    console.error('❌ Erro ao inicializar PDF:', err);
    this.handleError('Erro ao carregar o PDF');
  }
}

  private startLoadDetection(): void {
    // Método 1: Verificação periódica do elemento embed
    this.loadCheckInterval = setInterval(() => {
      this.checkIfPdfLoaded();
    }, 500);
    
    // Método 2: Timeout de fallback
    setTimeout(() => {
      if (this.isLoading) {
   //     console.log('⏰ Timeout - marcando como carregado');
        this.isLoading = false;
        this.checkEmbedSupport();
      }
    }, 3000);
  }

 private checkIfPdfLoaded(): void {
  if (!this.isLoading) {
    clearInterval(this.loadCheckInterval);
    return;
  }
  
  try {
    const iframeElement = this.pdfFrame?.nativeElement; // Mudou de pdfEmbed para pdfFrame
    
    if (iframeElement) {
      const rect = iframeElement.getBoundingClientRect();
      const hasVisibleSize = rect.width > 100 && rect.height > 100;
      const isVisible = iframeElement.offsetParent !== null;
      
      if (hasVisibleSize && isVisible) {
  //      console.log('✅ Iframe detectado como carregado e visível');
        this.onPdfLoaded();
        return;
      }
    }
  } catch (err) {
    // Ignora erros de cross-origin
  }
}

private checkEmbedSupport(): void {
  setTimeout(() => {
    const iframeElement = this.pdfFrame?.nativeElement; // Mudou de pdfEmbed para pdfFrame
    if (iframeElement) {
      const rect = iframeElement.getBoundingClientRect();
      const hasContent = rect.width > 10 && rect.height > 10;
      
      if (!hasContent) {
        console.log('⚠️ Iframe não está mostrando conteúdo - ativando fallback');
        this.showFallback = true;
        this.cdr.detectChanges();
      }
    }
  }, 1000);
}

 

  onPdfLoaded(): void {
 //   console.log('✅ PDF carregado com sucesso');
    this.isLoading = false;
    this.error = null;
    
    if (this.loadCheckInterval) {
      clearInterval(this.loadCheckInterval);
    }
    
  /*  this.messageService.add({
      severity: 'success',
      summary: 'PDF Carregado',
      detail: 'A credencial está pronta para visualização.',
      life: 2000
    });*/
    
    this.cdr.detectChanges();
  }

  downloadPdf(): void {
    if (!this.blob) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Arquivo PDF não disponível para download',
        life: 3000
      });
      return;
    }
    
    try {
      const url = URL.createObjectURL(this.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpa após 1 segundo
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
      this.messageService.add({
        severity: 'info',
        summary: 'Download Iniciado',
        detail: `Baixando "${this.fileName}"`,
        life: 3000
      });
      
    } catch (err) {
      console.error('❌ Erro no download:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível fazer o download',
        life: 3000
      });
    }
  }

  openInNewWindow(): void {
    if (this.blobUrl) {
      window.open(this.blobUrl, '_blank', 'noopener,noreferrer');
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private handleError(message: string): void {
    this.error = message;
    this.isLoading = false;
    this.cleanupTimers();
    this.cdr.detectChanges();
  }

  private cleanupTimers(): void {
    if (this.loadCheckInterval) {
      clearInterval(this.loadCheckInterval);
    }
    if (this.fallbackCheckTimeout) {
      clearTimeout(this.fallbackCheckTimeout);
    }
  }

  private cleanup(): void {
    this.cleanupTimers();
    
    // Revoga URL do blob
    if (this.blobUrl) {
      try {
        URL.revokeObjectURL(this.blobUrl);
        console.log('🗑️ Blob URL revogada');
      } catch (e) {
        console.warn('⚠️ Erro ao revogar blob URL:', e);
      }
    }
    
    this.blob = null;
    this.blobUrl = null;
  }

  close(): void {
    this.dialogRef.close();
  }
}